import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { chatScript } from '../data/chatScript';
import { questFilter, runPixelAction } from '../utils/pixelActions';
import type { BotLine, ChatMessage, ChatNode, ChatOption, ChatPhase } from '../types/chat';
import type { Emotion } from '../types/emotion';

interface ChatConversationParams {
  reduce: boolean;
  /** Messages are only sent while the bubbles can actually be seen */
  visibleRef: MutableRefObject<boolean>;
  onPhase: (phase: ChatPhase, emotion: Emotion) => void;
  onPulse: () => void;
  /** Which script to run (defaults to the scene conversations) */
  script?: ChatNode[];
  /** Pick the lines to say for a node (e.g. alternative lines on a revisit) */
  linesFor?: (node: ChatNode) => BotLine[];
}

/**
 * Runs the scripted conversation as quick bursts of short notes:
 * think (dots) → note pops in → next note pushes it up → … → reply options.
 * Choosing a reply (even mid-burst) cancels the rest and starts the next burst.
 */
export function useChatConversation({
  reduce,
  visibleRef,
  onPhase,
  onPulse,
  script = chatScript,
  linesFor,
}: ChatConversationParams) {
  const scriptRef = useRef(script);
  scriptRef.current = script;
  const linesForRef = useRef(linesFor);
  linesForRef.current = linesFor;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [burst, setBurst] = useState(0);
  const [thinking, setThinking] = useState(false);
  const [options, setOptions] = useState<ChatOption[]>([]);
  /** The burst is finished — options may show without the visitor asking */
  const [ready, setReady] = useState(false);
  /** The reply the visitor just tapped, shown briefly as their bubble */
  const [picked, setPicked] = useState<string | null>(null);
  const [ended, setEnded] = useState(false);

  const runRef = useRef(0);
  const idRef = useRef(0);
  const burstRef = useRef(0);
  const nodeRef = useRef<string | null>(null);
  const finishedRef = useRef(false);
  const interactedRef = useRef(false);
  const callbacks = useRef({ onPhase, onPulse });
  callbacks.current = { onPhase, onPulse };

  useEffect(
    () => () => {
      runRef.current++;
    },
    [],
  );

  // Haptics only after the visitor has touched something (browsers block it otherwise)
  const buzz = (pattern: number | number[]) => {
    if (interactedRef.current && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  };

  const runNode = useCallback(
    async (nodeId: string, run: number, delay: number) => {
      const alive = () => runRef.current === run;
      const sleep = (ms: number) => new Promise<boolean>((resolve) => setTimeout(() => resolve(alive()), ms));
      const waitVisible = async () => {
        while (!visibleRef.current) {
          if (!(await sleep(120))) return false;
        }
        return alive();
      };
      const node = scriptRef.current.find((n) => n.id === nodeId);
      if (!node) return;
      const lines = linesForRef.current ? linesForRef.current(node) : node.lines;

      nodeRef.current = nodeId;
      finishedRef.current = false;
      // In conversation from this moment: Pixel heads home and holds his spot so the bubbles can appear
      callbacks.current.onPhase('listening', 'curious');
      setEnded(false);
      setReady(false);
      setOptions(questFilter(node.options));
      const b = ++burstRef.current;
      setBurst(b); // previous burst pops away
      if (delay > 0 && !(await sleep(delay))) return;

      for (const line of lines) {
        if (!(await waitVisible())) return;
        // Thinking: coloured squares under the bubble, Pixel looks up and curls in
        setPicked(null);
        setThinking(true);
        callbacks.current.onPhase('thinking', 'curious');
        if (!(await sleep(reduce ? 200 : 456 + Math.min(line.text.length * 17, 720)))) return;
        if (!(await waitVisible())) return;

        // Send: the note pops in at the main spot, pushing the previous one up
        setThinking(false);
        const id = ++idRef.current;
        setMessages((m) => [...m.slice(-40), { id, from: 'bot', text: line.text, burst: b, at: Date.now() }]);
        callbacks.current.onPhase('speaking', line.emotion);
        callbacks.current.onPulse();
        buzz(8);
        if (!(await sleep(reduce ? 150 : 540))) return;

        // Beat to read it, holding the expression
        callbacks.current.onPhase('reacting', line.emotion);
        if (!(await sleep(reduce ? 100 : Math.min(360 + line.text.length * 17, 1080)))) return;
      }

      setThinking(false);
      finishedRef.current = true;
      const last = lines[lines.length - 1];
      if (node.options.length === 0) {
        // Still holds his spot — he only flies off once the notes have popped away
        callbacks.current.onPhase('listening', last.emotion);
        setEnded(true);
      } else {
        callbacks.current.onPhase('listening', last.emotion === 'sad' ? 'sad' : 'curious');
      }
      setReady(true);
    },
    [reduce, visibleRef],
  );

  /** Play a node (cancels whatever is running). */
  const play = useCallback(
    (nodeId: string, delay = 0) => {
      runNode(nodeId, ++runRef.current, delay);
    },
    [runNode],
  );

  const choose = useCallback(
    (option: ChatOption) => {
      interactedRef.current = true;
      const run = ++runRef.current;
      setThinking(false);
      setPicked(option.label);
      setMessages((m) => [
        ...m.slice(-40),
        { id: ++idRef.current, from: 'user', text: option.label, burst: burstRef.current, at: Date.now() },
      ]);
      callbacks.current.onPhase('reacting', option.reaction);
      buzz([6, 40, 6]);
      if (option.action) runPixelAction(option.action);
      runNode(option.next, run, reduce ? 200 : 700);
    },
    [runNode, reduce],
  );

  /** Stop talking (bubbles hidden). Remembers whether the burst was cut short. */
  const pause = useCallback(() => {
    runRef.current++;
    setThinking(false);
    setPicked(null);
    callbacks.current.onPhase('idle', 'neutral');
  }, []);

  /** Pick the thread back up. Returns false if there's nothing to resume yet. */
  const resume = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return false;
    if (!finishedRef.current) {
      play(node, 300); // cut short — say it again
    } else {
      const ok = scriptRef.current.find((n) => n.id === node)?.options.length;
      callbacks.current.onPhase(ok ? 'listening' : 'idle', 'curious');
    }
    return true;
  }, [play]);

  /** Close button: everything pops away, Pixel goes quiet (and picks it up again later). */
  const dismiss = useCallback(() => {
    interactedRef.current = true;
    runRef.current++;
    finishedRef.current = true;
    setThinking(false);
    setPicked(null);
    setReady(false);
    setOptions([]);
    setBurst(++burstRef.current);
    setEnded(true);
    callbacks.current.onPhase('idle', 'neutral');
  }, []);

  const markInteracted = useCallback(() => {
    interactedRef.current = true;
  }, []);

  return {
    messages,
    burst,
    thinking,
    options,
    ready,
    picked,
    ended,
    play,
    choose,
    pause,
    resume,
    dismiss,
    markInteracted,
  };
}
